import type { Options } from 'pro-gallery-lib';
import {
  PgLayoutImageCropTypeOptions,
  PgLayoutImageResizingModeOptions,
  isLayoutPGSideBySide,
  Layout,
  PgLayoutCardsRowTypeOptions,
  type VideoSettings,
} from '@wix/communities-blog-client-common';
import {
  HOVERING_BEHAVIOUR,
  CUBE_TYPE,
  TEXT_PLACEMENT,
  OPTION_GALLERY_SIZE_TYPE,
  TEXT_BOX_WIDTH_CALCULATION_OPTIONS,
  type LayoutImageRatio,
  type TextBoxAlignment,
} from '../../constants/pro-gallery-options';
import type { Color } from '../../types';

export type ProGalleryOptions = {
  containerWidth: number;
  isRTL: boolean;
  layoutAutoHeightEnabled: boolean;
  layoutCardsRowType: PgLayoutCardsRowTypeOptions;
  layoutContentHeight: number;
  layoutImageCropType: PgLayoutImageCropTypeOptions;
  layoutImageProportions: number;
  layoutImageRatio: LayoutImageRatio;
  layoutImageResizingMode: PgLayoutImageResizingModeOptions | undefined;
  layoutImageWidth: number;
  layoutPostSize: number;
  layoutPostsPerRow: number;
  layoutSpacing: number;
  textBoxAlignment: TextBoxAlignment;
  videoSettings: VideoSettings;
  hideCoverImage?: boolean;
  arrowsSize?: number;
  showArrows?: boolean;
  arrowsPosition?: number;
  arrowsColor?: string | Color;
  autoSlide?: boolean;
  pauseTime?: number;
  loop?: boolean;
};

export const getLayoutImageRatio = (
  layout: Layout | undefined,
  options: ProGalleryOptions,
  cropType: PgLayoutImageCropTypeOptions,
) => {
  if (options.hideCoverImage && options.layoutAutoHeightEnabled) {
    return 99;
  }

  if (`${cropType}` !== PgLayoutImageCropTypeOptions.Max) {
    return options.layoutImageRatio!;
  }

  if (isLayoutPGSideBySide(layout)) {
    return 4 / 3;
  }

  return 16 / 9;
};

type ItemBorder = {
  width: number;
  color?: string;
};

export type ProGallery5Options = Options & {
  gallerySizeType: (typeof OPTION_GALLERY_SIZE_TYPE)[keyof typeof OPTION_GALLERY_SIZE_TYPE];
  gallerySizePx: number;
  itemBorderColor: string | undefined;
  imageMargin: number;
  isRTL: boolean;
  allowTitle: boolean;
  gridStyle: number;
  numberOfImagesPerRow: number;
  layoutParams_crop_ratios: number[];
  textBoxWidth: number;
  calculateTextBoxWidthMode: (typeof TEXT_BOX_WIDTH_CALCULATION_OPTIONS)[keyof typeof TEXT_BOX_WIDTH_CALCULATION_OPTIONS];
  textBoxWidthPercent: number;
  galleryLayout: Layout;
  hoveringBehaviour: (typeof HOVERING_BEHAVIOUR)[keyof typeof HOVERING_BEHAVIOUR];
  titlePlacement: string;
  cubeRatio: number;
  cubeType: (typeof CUBE_TYPE)[keyof typeof CUBE_TYPE];
  showVideoPlayButton: boolean;
  isVertical: boolean;
};

export const createOptions = (
  layout: Layout | undefined,
  options: ProGalleryOptions,
  itemBorder: ItemBorder,
): ProGallery5Options => {
  const galleryWidth = options.containerWidth - itemBorder.width;
  const imageMargin = -(galleryWidth > 0 ? itemBorder.width : itemBorder.width - 1 + galleryWidth);

  const commonGalleryOptions: Partial<ProGallery5Options> = {
    stylingParams_itemBorderWidth: itemBorder.width,
    stylingParams_itemBorderColor: itemBorder.color,
    layoutParams_targetItemSize_unit: 'PIXEL',
    layoutParams_targetItemSize_value: options.layoutPostSize,
    layoutParams_structure_itemSpacing:
      options.layoutSpacing === 0 ? imageMargin : options.layoutSpacing,
    layoutParams_info_layout: 'ATTACHED_BACKGROUND',
    behaviourParams_gallery_blockContextMenu: false,
    behaviourParams_gallery_layoutDirection: options.isRTL ? 'RIGHT_TO_LEFT' : 'LEFT_TO_RIGHT',
    behaviourParams_item_overlay_hoverAnimation: 'NO_EFFECT',
    behaviourParams_item_content_loader: 'BLUR',
    behaviourParams_item_video_playTrigger:
      options.videoSettings.play === 'hover'
        ? 'HOVER'
        : options.videoSettings.play === 'onClick'
        ? 'CLICK'
        : 'AUTO',
    behaviourParams_item_video_enablePlayButton: options.videoSettings.showPlayButton,
    behaviourParams_item_video_loop: options.videoSettings.loop,
    behaviourParams_item_video_speed: Number(options.videoSettings.playbackSpeed),
    behaviourParams_item_clickAction: 'NOTHING',
    // ---- Legacy options for layout fixer
    gallerySizeType: OPTION_GALLERY_SIZE_TYPE.PX,
    gallerySizePx: options.layoutPostSize,
    itemBorderColor: itemBorder.color,
    imageMargin: options.layoutSpacing === 0 ? imageMargin : options.layoutSpacing,
    isRTL: options.isRTL,
    allowTitle: true,
    showVideoPlayButton: options.videoSettings.showPlayButton,
  };

  const cardsRowOptions: Partial<ProGallery5Options> =
    options.layoutCardsRowType === PgLayoutCardsRowTypeOptions.ItemsPerRow &&
    options.layoutPostsPerRow
      ? {
          ...commonGalleryOptions,
          layoutParams_structure_numberOfColumns: options.layoutPostsPerRow,
          layoutParams_structure_responsiveMode: 'SET_ITEMS_PER_ROW',
          // ---- Legacy options for layout fixer
          gridStyle: 1,
          numberOfImagesPerRow: options.layoutPostsPerRow,
        }
      : {};

  const cubeRatio = getLayoutImageRatio(layout, options, options.layoutImageCropType);
  const cubeTypeAndCubeRatio: Partial<ProGallery5Options> =
    `${options.layoutImageCropType}` === PgLayoutImageCropTypeOptions.Max
      ? {
          ...commonGalleryOptions,
          layoutParams_crop_method: 'MAX',
          layoutParams_crop_ratios: [cubeRatio],
        }
      : {
          ...commonGalleryOptions,
          layoutParams_crop_method: 'FILL',
          layoutParams_crop_ratios: [cubeRatio],
        };

  const textBoxOptions: Partial<ProGallery5Options> =
    options.layoutImageResizingMode === PgLayoutImageResizingModeOptions.Fixed
      ? {
          ...commonGalleryOptions,
          layoutParams_info_width: options.layoutPostSize - options.layoutImageWidth,
          layoutParams_info_sizeUnits: 'PIXEL',
          // ---- Legacy options
          textBoxWidth: options.layoutPostSize - options.layoutImageWidth,
          calculateTextBoxWidthMode: TEXT_BOX_WIDTH_CALCULATION_OPTIONS.MANUAL,
        }
      : {
          ...commonGalleryOptions,
          layoutParams_info_width: Math.abs(options.layoutImageProportions - 100),
          layoutParams_info_sizeUnits: 'PERCENT',
          // ---- Legacy options
          textBoxWidthPercent: Math.abs(options.layoutImageProportions - 100),
          calculateTextBoxWidthMode: TEXT_BOX_WIDTH_CALCULATION_OPTIONS.PERCENT,
        };

  switch (layout) {
    case Layout.PgTextOnImageSmall:
    case Layout.PgTextOnImageMedium:
    case Layout.PgTextOnImageLarge:
      return {
        ...commonGalleryOptions,
        ...cardsRowOptions,
        layoutParams_structure_galleryLayout: 2,
        layoutParams_structure_groupsOrder: 'LEFT_TO_RIGHT',
        layoutParams_info_placement: 'OVERLAY',
        behaviourParams_item_overlay_hoveringBehaviour: 'ALWAYS_SHOW',
        layoutParams_crop_ratios: [cubeRatio],
        // ---- Legacy options
        galleryLayout: 2,
        hoveringBehaviour: HOVERING_BEHAVIOUR.NO_CHANGE,
        titlePlacement: TEXT_PLACEMENT.SHOW_ON_HOVER,
        cubeRatio,
        cubeType: CUBE_TYPE.FILL,
      } as ProGallery5Options;
    case Layout.PgSideBySide:
    case Layout.PgSideBySideRight:
      return {
        ...commonGalleryOptions,
        ...textBoxOptions,
        ...cubeTypeAndCubeRatio,
        layoutParams_structure_galleryLayout: 2,
        layoutParams_structure_groupsOrder: 'LEFT_TO_RIGHT',
        layoutParams_info_placement: getInfoPlacement(layout, options),
        behaviourParams_item_overlay_hoveringBehaviour: 'NEVER_SHOW',
        layoutParams_structure_numberOfColumns: 1,
        // ---- Legacy options
        galleryLayout: 2,
        hoveringBehaviour: HOVERING_BEHAVIOUR.NEVER_SHOW,
        titlePlacement: options.textBoxAlignment,
        numberOfImagesPerRow: 1,
        gridStyle: 1,
        cubeRatio,
      } as ProGallery5Options;
    case Layout.PgCardMedium:
      return {
        ...commonGalleryOptions,
        ...cardsRowOptions,
        layoutParams_structure_galleryLayout: 1,
        layoutParams_structure_layoutOrientation: 'VERTICAL',
        layoutParams_info_placement: 'BELOW',
        layoutParams_info_height: options.layoutContentHeight,
        behaviourParams_item_overlay_hoveringBehaviour: 'NEVER_SHOW',
        // ---- Legacy options
        galleryLayout: 1,
        hoveringBehaviour: HOVERING_BEHAVIOUR.NEVER_SHOW,
        titlePlacement: TEXT_PLACEMENT.SHOW_BELOW,
        isVertical: true,
        textBoxHeight: options.layoutContentHeight,
      } as ProGallery5Options;
    case Layout.PgOneColumn:
      return {
        ...commonGalleryOptions,
        layoutParams_structure_galleryLayout: 2,
        layoutParams_structure_groupsOrder: 'LEFT_TO_RIGHT',
        layoutParams_structure_layoutOrientation: 'VERTICAL',
        layoutParams_info_placement: 'BELOW',
        layoutParams_info_height: options.layoutContentHeight,
        behaviourParams_item_overlay_hoveringBehaviour: 'NEVER_SHOW',
        layoutParams_crop_method: 'MIN',
        layoutParams_crop_ratios: [cubeRatio],
        // ---- Legacy options
        galleryLayout: 2,
        hoveringBehaviour: HOVERING_BEHAVIOUR.NEVER_SHOW,
        titlePlacement: TEXT_PLACEMENT.SHOW_BELOW,
        cubeRatio,
        cubeType: CUBE_TYPE.MIN,
        isVertical: true,
        textBoxHeight: options.layoutContentHeight,
      } as ProGallery5Options;
    case Layout.Slider:
      return {
        ...commonGalleryOptions,
        ...cubeTypeAndCubeRatio,
        layoutParams_structure_galleryLayout: 5,
        layoutParams_structure_layoutOrientation: 'VERTICAL',
        layoutParams_navigationArrows_verticalAlignment: 'IMAGE_CENTER',
        layoutParams_crop_ratios: [cubeRatio],
        layoutParams_navigationArrows_position:
          options.arrowsPosition === 0
            ? 'ON_GALLERY'
            : options.arrowsPosition === 1
            ? 'OUTSIDE_GALLERY'
            : 'MOUSE_CURSOR',
        layoutParams_navigationArrows_size: options.arrowsSize,
        layoutParams_info_height: options.layoutContentHeight,
        behaviourParams_item_overlay_hoveringBehaviour: 'NEVER_SHOW',
        // ---- Legacy options
        galleryLayout: 5,
        cubeRatio,
        isVertical: true,
        showArrows: options.showArrows,
        slideshowInfoSize: options.layoutContentHeight,
        arrowsSize: options.arrowsSize,
        isAutoSlideshow: options.autoSlide,
        autoSlideshowInterval: options.pauseTime,
        autoSlideshowType: 'interval',
        arrowsPosition: options.arrowsPosition,
        arrowsColor:
          typeof options.arrowsColor === 'string'
            ? options.arrowsColor
            : options.arrowsColor?.value,
        slideshowLoop: options.loop,
      } as ProGallery5Options;
    case Layout.List:
    case Layout.ListMedium:
    case Layout.ListLarge:
      return {
        ...commonGalleryOptions,
        ...cardsRowOptions,
        ...textBoxOptions,
        ...cubeTypeAndCubeRatio,
        layoutParams_structure_galleryLayout: 2,
        layoutParams_structure_groupsOrder: 'LEFT_TO_RIGHT',
        layoutParams_structure_layoutOrientation: 'VERTICAL',
        layoutParams_info_placement: getInfoPlacement(layout, options),
        layoutParams_info_height: options.layoutContentHeight,
        behaviourParams_item_overlay_hoveringBehaviour: 'NEVER_SHOW',
        layoutParams_structure_numberOfColumns: 1,
        // ---- Legacy options
        galleryLayout: 2,
        hoveringBehaviour: HOVERING_BEHAVIOUR.NEVER_SHOW,
        titlePlacement: `SHOW_BELOW,${options.textBoxAlignment}`,
        numberOfImagesPerRow: 1,
        cubeRatio,
        isVertical: true,
        textBoxHeight: options.layoutContentHeight,
      } as ProGallery5Options;
    case Layout.PgGrid:
    case Layout.PgGridIntermediate:
    case Layout.PgGridLarge:
    default:
      return {
        ...commonGalleryOptions,
        ...cardsRowOptions,
        ...cubeTypeAndCubeRatio,
        layoutParams_structure_galleryLayout: 2,
        layoutParams_structure_groupsOrder: 'LEFT_TO_RIGHT',
        layoutParams_structure_layoutOrientation: 'VERTICAL',
        layoutParams_info_placement: 'BELOW',
        layoutParams_info_height: options.layoutContentHeight,
        behaviourParams_item_overlay_hoveringBehaviour: 'NEVER_SHOW',
        // ---- Legacy options
        galleryLayout: 2,
        hoveringBehaviour: HOVERING_BEHAVIOUR.NEVER_SHOW,
        titlePlacement: TEXT_PLACEMENT.SHOW_BELOW,
        cubeRatio,
        isVertical: true,
        textBoxHeight: options.layoutContentHeight,
      } as ProGallery5Options;
  }
};

function getInfoPlacement(layout: Layout, options: ProGalleryOptions) {
  if (!options.textBoxAlignment) {
    return undefined;
  }

  const sideBySide = [Layout.PgSideBySide, Layout.PgSideBySideRight].includes(layout);
  const list = [Layout.List, Layout.ListMedium, Layout.ListLarge].includes(layout);

  if (sideBySide) {
    if (options.textBoxAlignment === 'SHOW_ON_THE_LEFT') {
      return options.isRTL ? 'RIGHT' : 'LEFT';
    } else {
      return options.isRTL ? 'LEFT' : 'RIGHT';
    }
  }

  if (list) {
    if (options.textBoxAlignment === 'SHOW_ON_THE_LEFT') {
      return options.isRTL ? 'BELOW,RIGHT' : 'BELOW,LEFT';
    } else {
      return options.isRTL ? 'BELOW,LEFT' : 'BELOW,RIGHT';
    }
  }
}
